import React, { useState, useEffect } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import Loading from "../utils/Loading";
import { getRoleDisplay } from "../utils/getRoleDisplay";
import { USERS_BASE_URL } from "../services/SignUpService";
import Swal from "sweetalert2";
import axios from "axios";
import { useAuthContext } from "../hooks/useAuthContext";

export const UserProfile = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user: currentUser } = useAuthContext();
  const [isEditingPermissions, setIsEditingPermissions] = useState(false);

  const canEditProfile = (user) => {
    if (!user || !currentUser) return false;

    // Current user must be an admin
    const isCurrentUserAdmin =
      currentUser.isAdmin ||
      currentUser.role?.includes("Admin") ||
      currentUser.role?.includes("SuperAdmin");

    // Cannot edit your own admin privileges (security measure)
    const isNotSelf = user._id !== currentUser._id;

    return isCurrentUserAdmin && isNotSelf;
  };
  const [user, setUser] = useState({
    photo: "",
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    isAdmin: "",
    isVerified: "",
    createdAt: "",
  });

  const { id } = useParams();

  const deleteUser = async (e, _id) => {
    e.preventDefault();
    try {
      const confirmResult = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (confirmResult.isConfirmed) {
        // Use currentUser from component scope
        await axios.delete(`${USERS_BASE_URL}/api/users/list/delete/${_id}`, {
          headers: {
            Authorization: `Bearer ${currentUser?.token}`,
          },
        });
        Swal.fire({
          title: "Deleted!",
          text: "User has been deleted.",
          icon: "success",
          timer: 2000,
        });
        navigate("/api/dashboard/users");
      }
    } catch (error) {
      console.error("Error deleting user: ", error);
      Swal.fire({
        title: "Error!",
        text: `Failed to delete user! Error: ${error.message}`,
        icon: "error",
        timer: 2000,
      });
    }
  };

  const fetchCourseData = async () => {
    try {
      const response = await fetch(`${USERS_BASE_URL}/api/users/list/${id}`);
      if (!response.ok) {
        throw new Error("Failed to fetch course data");
      }
      const data = await response.json();
      setUser(data);
      setLoading(false);
    } catch (err) {
      setError(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseData();
  }, [id]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="alert alert-danger mx-auto mt-5"
        style={{ maxWidth: "600px" }}
      >
        {error.message}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center mt-5">
        <p className="fs-4">No user found!</p>
      </div>
    );
  }

  // Toggle admin status
  const toggleAdminStatus = async (currentStatus) => {
    try {
      const result = await Swal.fire({
        title: `${currentStatus ? "Remove" : "Grant"} admin privileges?`,
        text: `Are you sure you want to ${
          currentStatus ? "remove admin privileges from" : "make"
        } this user ${currentStatus ? "" : "an admin"}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, proceed",
      });

      if (result.isConfirmed) {
        setLoading(true);
        const response = await axios.put(
          `${USERS_BASE_URL}/api/users/list/update/${user._id}`,
          { isAdmin: !currentStatus },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${currentUser?.token}`,
            },
          }
        );

        setUser((prev) => ({ ...prev, isAdmin: !currentStatus }));

        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Success",
          text: "Admin status updated successfully!",
          showConfirmButton: false,
          timer: 1500,
        });

        setLoading(false);
      }
    } catch (error) {
      console.error("Error toggling admin status:", error);
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || "Failed to update admin status",
      });
    }
  };

  // Toggle verification status
  const toggleVerificationStatus = async (currentStatus) => {
    try {
      const result = await Swal.fire({
        title: `${currentStatus ? "Remove" : "Grant"} verification status?`,
        text: `Are you sure you want to mark this user as ${
          currentStatus ? "unverified" : "verified"
        }?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, proceed",
      });

      if (result.isConfirmed) {
        setLoading(true);
        const response = await axios.put(
          `${USERS_BASE_URL}/api/users/list/update/${user._id}`,
          { isVerified: !currentStatus },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${currentUser?.token}`,
            },
          }
        );

        setUser((prev) => ({ ...prev, isVerified: !currentStatus }));

        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Success",
          text: "Verification status updated successfully!",
          showConfirmButton: false,
          timer: 1500,
        });

        setLoading(false);
      }
    } catch (error) {
      console.error("Error toggling verification status:", error);
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error.response?.data?.message ||
          "Failed to update verification status",
      });
    }
  };

  // Handle role change
  const handleRoleChange = async (role, currentlyHasRole) => {
    try {
      let updatedRoles;
      if (currentlyHasRole) {
        // Remove role
        updatedRoles = user.role.filter((r) => r !== role);
      } else {
        // Add role
        updatedRoles = [...(user.role || []), role];
      }

      setLoading(true);
      const response = await axios.put(
        `${USERS_BASE_URL}/api/users/list/update/${user._id}`,
        { role: updatedRoles },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentUser?.token}`,
          },
        }
      );

      setUser((prev) => ({ ...prev, role: updatedRoles }));

      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "Success",
        text: "User roles updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });

      setLoading(false);
    } catch (error) {
      console.error("Error updating user role:", error);
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || "Failed to update user roles",
      });
    }
  };

  return (
    <section className="profile">
      <div className="container py-4">
        <div className="row mb-4">
          <div className="col">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white border-0">
                <h2 className="h4 mb-0 text-primary">User Profile</h2>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {/* Profile Card */}
          <div className="col-lg-4">
            <div className="card shadow-sm h-100">
              <div className="card-body text-center p-4">
                <div className="position-relative d-inline-block mb-3">
                  {/* display loading spinner if image is not loaded  else display image */}
                  {loading ? (
                    <>
                      <div
                        className="spinner-border text-primary"
                        role="status"
                        style={{ width: "3rem", height: "3rem" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </>
                  ) : (
                    <img
                      // src={user.avatar || "https://i.pravatar.cc/150"}
                      src={
                        user.photo || "https://picsum.photos/150/150?grayscale"
                      }
                      alt="Profile"
                      className="rounded-circle shadow-sm"
                      width="150"
                      height="150"
                    />
                  )}
                  {user.isVerified && (
                    <span className="position-absolute bottom-0 end-0 bg-success p-2 rounded-circle">
                      <i className="fa fa-check text-white"></i>
                    </span>
                  )}
                </div>
                <h3 className="h5 mb-1">
                  {user.firstName} {user.lastName}
                </h3>
                {/* <p className="text-muted mb-3">
                  {user.isAdmin ? user.role.join(", ") : user.role}
                </p> */}
                <p className="text-muted mb-3">
                  {Array.isArray(user.role) ? user.role.join(", ") : user.role}
                </p>

                {/* Display role badges */}
                {user.role &&
                  Array.isArray(user.role) &&
                  user.role.length > 0 && (
                    <div className="d-flex justify-content-center flex-wrap gap-1 mb-3">
                      {user.role.map((role, index) => (
                        <span
                          key={index}
                          className={`badge ${
                            role === "Admin"
                              ? "bg-primary"
                              : role === "SuperAdmin"
                              ? "bg-danger"
                              : role === "Developer"
                              ? "bg-info"
                              : "bg-secondary"
                          }`}
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  )}

                <div className="d-flex justify-content-center gap-2 mb-3">
                  {currentUser &&
                  currentUser.isAdmin &&
                  user._id !== currentUser._id ? (
                    <>
                      <button
                        className={`badge border-0 ${
                          user.isAdmin ? "bg-success" : "bg-secondary"
                        }`}
                        onClick={() => toggleAdminStatus(user.isAdmin)}
                        style={{ cursor: "pointer" }}
                      >
                        {user.isAdmin ? "Admin" : "User"}
                      </button>
                      <button
                        className={`badge border-0 ${
                          user.isVerified ? "bg-success" : "bg-warning"
                        }`}
                        onClick={() =>
                          toggleVerificationStatus(user.isVerified)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        {user.isVerified ? "Verified" : "Unverified"}
                      </button>
                    </>
                  ) : (
                    <>
                      <span
                        className={`badge ${
                          user.isAdmin ? "bg-success" : "bg-secondary"
                        }`}
                      >
                        {user.isAdmin ? "Admin" : "User"}
                      </span>
                      <span
                        className={`badge ${
                          user.isVerified ? "bg-success" : "bg-warning"
                        }`}
                      >
                        {user.isVerified ? "Verified" : "Unverified"}
                      </span>
                    </>
                  )}
                </div>

                <hr className="my-3" />

                <div className="text-start">
                  <div className="d-flex align-items-center mb-2">
                    <i className="fa fa-envelope text-muted me-2"></i>
                    <span>{user.email}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="fa fa-calendar text-muted me-2"></i>
                    <span>
                      Joined:{" "}
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : ""}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Details Card */}
          <div className="col-lg-8">
            <div className="card shadow-sm h-100">
              <div className="card-body p-4">
                <h3 className="h5 mb-4">User Details</h3>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">First Name</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fa fa-user"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        value={user.firstName || ""}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Last Name</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fa fa-user"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        value={user.lastName || ""}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="col-12">
                    <label className="form-label">Email</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fa fa-envelope"></i>
                      </span>
                      <input
                        type="email"
                        className="form-control"
                        value={user.email || ""}
                        disabled
                      />
                    </div>
                  </div>
                  {/*   <p className="text-muted mb-3">
                  {Array.isArray(user.role) ? user.role.join(", ") : user.role}
                </p> */}
                  <div className="col-12">
                    <label className="form-label">Role</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fa fa-shield"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          Array.isArray(user.role)
                            ? user.role.join(", ")
                            : user.role
                        }
                        // value={user.isAdmin ? user.role.join(", ") : user.role}
                        disabled
                      />
                    </div>
                  </div>

                  {/* Display roles as badges */}
                  {user.role &&
                    Array.isArray(user.role) &&
                    user.role.length > 0 && (
                      <div className="col-12 mt-2">
                        <label className="form-label">Role Privileges</label>
                        <div className="p-2 border rounded">
                          <div className="d-flex flex-wrap gap-2">
                            {user.role.map((role, index) => (
                              <div
                                key={index}
                                className="d-flex align-items-center"
                              >
                                <span
                                  className={`badge ${
                                    role === "Admin"
                                      ? "bg-primary"
                                      : role === "SuperAdmin"
                                      ? "bg-danger"
                                      : role === "Developer"
                                      ? "bg-info"
                                      : "bg-secondary"
                                  } me-1`}
                                >
                                  {role}
                                </span>
                                {role === "Admin" && (
                                  <small className="text-muted">
                                    - Can manage users
                                  </small>
                                )}
                                {role === "SuperAdmin" && (
                                  <small className="text-muted">
                                    - Full system access
                                  </small>
                                )}
                                {role === "Developer" && (
                                  <small className="text-muted">
                                    - Technical access
                                  </small>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                </div>

                <div className="d-flex flex-wrap gap-3 mt-5 pt-3 border-top">
                  <Link
                    to="/api/dashboard/users"
                    className="btn btn-outline-secondary"
                  >
                    <i className="fa fa-chevron-left me-2"></i> Back
                  </Link>

                  {currentUser &&
                    currentUser.isAdmin &&
                    user._id !== currentUser._id && (
                      <button
                        className="btn btn-primary"
                        onClick={() =>
                          setIsEditingPermissions(!isEditingPermissions)
                        }
                      >
                        <i className="fa fa-shield me-2"></i> Manage Permissions
                      </button>
                    )}

                  {canEditProfile(user) ? (
                    <Link
                      to={`/api/dashboard/update-user/${user._id}`}
                      className="btn btn-outline-primary"
                    >
                      <i className="fa fa-pencil me-2"></i> Edit Profile
                    </Link>
                  ) : (
                    <button className="btn btn-secondary" disabled>
                      <i className="fa fa-pencil me-2"></i> Edit Disabled
                    </button>
                  )}
                  <button
                    className="btn btn-outline-danger"
                    onClick={(e) => deleteUser(e, user._id)}
                    disabled={!canEditProfile(user)}
                  >
                    <i className="fa fa-trash me-2"></i>{" "}
                    {canEditProfile(user)
                      ? "Delete Account"
                      : "Delete Disabled"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions Management Card (Only visible to admins viewing other users) */}
          {currentUser &&
            currentUser.isAdmin &&
            user._id !== currentUser._id && (
              <div className="col-lg-8">
                <div className="card shadow-sm mb-4">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <i className="fa fa-shield me-2 text-primary"></i> User
                      Permissions
                    </h5>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() =>
                        setIsEditingPermissions(!isEditingPermissions)
                      }
                    >
                      {isEditingPermissions ? (
                        <>
                          <i className="fa fa-times me-1"></i> Cancel
                        </>
                      ) : (
                        <>
                          <i className="fa fa-pencil me-1"></i> Edit Permissions
                        </>
                      )}
                    </button>
                  </div>
                  <div className="card-body">
                    {isEditingPermissions ? (
                      <div className="row g-3">
                        {/* Admin Status */}
                        <div className="col-md-6">
                          <div className="card h-100 border">
                            <div className="card-body">
                              <h6 className="card-title d-flex justify-content-between align-items-center">
                                Admin Status
                                <div className="form-check form-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="adminSwitch"
                                    checked={user.isAdmin || false}
                                    onChange={() =>
                                      toggleAdminStatus(user.isAdmin)
                                    }
                                  />
                                </div>
                              </h6>
                              <p className="card-text small text-muted">
                                Admins have full access to manage users and
                                system settings.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Verification Status */}
                        <div className="col-md-6">
                          <div className="card h-100 border">
                            <div className="card-body">
                              <h6 className="card-title d-flex justify-content-between align-items-center">
                                Verification Status
                                <div className="form-check form-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="verificationSwitch"
                                    checked={user.isVerified || false}
                                    onChange={() =>
                                      toggleVerificationStatus(user.isVerified)
                                    }
                                  />
                                </div>
                              </h6>
                              <p className="card-text small text-muted">
                                Verified users have confirmed their email
                                address.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* User Roles */}
                        <div className="col-12">
                          <div className="card border">
                            <div className="card-body">
                              <h6 className="card-title mb-3">User Roles</h6>
                              <div className="row g-2">
                                {[
                                  "Admin",
                                  "SuperAdmin",
                                  "Developer",
                                  "Manager",
                                  "Editor",
                                  "Viewer",
                                ].map((role) => (
                                  <div key={role} className="col-md-4 col-sm-6">
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`role-${role}`}
                                        checked={
                                          user.role?.includes(role) || false
                                        }
                                        onChange={() =>
                                          handleRoleChange(
                                            role,
                                            user.role?.includes(role) || false
                                          )
                                        }
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor={`role-${role}`}
                                      >
                                        {role}
                                      </label>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 text-end">
                          <button
                            className="btn btn-outline-secondary me-2"
                            onClick={() => setIsEditingPermissions(false)}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={() => setIsEditingPermissions(false)}
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="row g-3">
                        {/* Admin Status */}
                        <div className="col-md-6">
                          <div className="d-flex align-items-center mb-2">
                            <span
                              className={`badge me-2 ${
                                user.isAdmin ? "bg-success" : "bg-secondary"
                              }`}
                            >
                              {user.isAdmin ? "Admin" : "Not Admin"}
                            </span>
                            <span>Admin Status</span>
                          </div>
                        </div>

                        {/* Verification Status */}
                        <div className="col-md-6">
                          <div className="d-flex align-items-center mb-2">
                            <span
                              className={`badge me-2 ${
                                user.isVerified ? "bg-success" : "bg-warning"
                              }`}
                            >
                              {user.isVerified ? "Verified" : "Unverified"}
                            </span>
                            <span>Verification Status</span>
                          </div>
                        </div>

                        {/* User Roles */}
                        <div className="col-12">
                          <h6 className="mb-2">User Roles</h6>
                          <div className="d-flex flex-wrap gap-2">
                            {user.role &&
                            Array.isArray(user.role) &&
                            user.role.length > 0 ? (
                              user.role.map((role, index) => (
                                <span
                                  key={index}
                                  className={`badge ${
                                    role === "Admin"
                                      ? "bg-primary"
                                      : role === "SuperAdmin"
                                      ? "bg-danger"
                                      : role === "Developer"
                                      ? "bg-info"
                                      : "bg-secondary"
                                  }`}
                                >
                                  {role}
                                </span>
                              ))
                            ) : (
                              <span className="text-muted">
                                No roles assigned
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
    </section>
  );
};
